import { Group, Stack } from '@mantine/core';
import {
	useListCustomProperties,
	type SecodaEntityPropertySnapshot,
	type VersionHistoryOut,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import equal from 'fast-deep-equal';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useIntegrationPrefetched, type ISecodaEntity } from '../../api';
import { CollectionsPropertySelector } from '../Properties/CollectionsPropertySelector';
import { CustomPropertiesPropertySelector } from '../Properties/CustomPropertySelector';
import { GovernancePropertySelector } from '../Properties/GovernancePropertySelector';
import { OwnersPropertySelector } from '../Properties/OwnersPropertySelector';
import { StatusPropertySelector } from '../Properties/StatusPropertySelector';
import TagPropertySelector from '../Properties/TagPropertySelector';
import { TeamsPropertySelector } from '../Properties/TeamsPropertySelector';
import { VerificationPropertySelector } from '../Properties/VerificationPropertySelector';
import { PropertyWrapper } from './PropertyWrapper';

const VALID_PROPERTIES: (keyof SecodaEntityPropertySnapshot)[] = [
	'published',
	'tags',
	'collections',
	'owners',
	'owners_groups',
	'pii',
	'verified',
	'teams',
	'properties2',
];

export interface SnapshotPropertiesProps {
	versionHistory: VersionHistoryOut;
	integrationId?: string;
	relevantChanges?: string[];
	currentEntity?: ISecodaEntity;
}

export function SnapshotProperties({
	versionHistory,
	integrationId,
	relevantChanges,
	currentEntity,
}: SnapshotPropertiesProps) {
	const snapshot = versionHistory.metadata.secoda_entity_property_snapshot;

	const { data: integration } = useIntegrationPrefetched({
		id: integrationId ?? '',
		options: {
			enabled: Boolean(integrationId),
		},
	});

	const shouldShowAsChanged = (
		backendFieldName: string,
		fallbackCondition: boolean
	) => {
		if (!relevantChanges || relevantChanges.length === 0) {
			return fallbackCondition;
		}

		return relevantChanges.includes(backendFieldName);
	};

	const previousValues = versionHistory.metadata.prev;
	const currentValues = versionHistory.metadata.current;

	const baseEntity = snapshot ?? {
		teams: currentValues.team_ids,
		tags: currentValues.tag_ids,
		collections: currentValues.collection_ids,
		owners: currentValues.owner_ids,
		owners_groups: currentValues.owner_group_ids,
		pii: currentValues.pii,
		verified: currentValues.verified,
		published: currentValues.published,
		properties2: currentValues.custom_properties,
	};

	const consolidatedEntity = {
		...baseEntity,
		// Override with current entity data for fields that are marked as changed
		...(currentEntity &&
			currentEntity.tags &&
			(relevantChanges?.includes('tag_ids') ||
				currentEntity.tags.length > 0) && { tags: currentEntity.tags }),
		// Always prefer latest booleans when provided
		...(currentEntity &&
			typeof currentEntity.verified === 'boolean' && {
				verified: currentEntity.verified,
			}),
		...(currentEntity &&
			typeof currentEntity.pii === 'boolean' && {
				pii: currentEntity.pii,
			}),
		...(currentEntity &&
			typeof currentEntity.published === 'boolean' && {
				published: currentEntity.published,
			}),
		...(currentEntity &&
			(relevantChanges?.includes('team_ids') ||
				(currentEntity.teams && currentEntity.teams.length > 0)) && {
				teams: currentEntity.teams,
			}),
		...(currentEntity &&
			((relevantChanges?.includes('owner_ids') ?? false) ||
				(currentEntity.owners && currentEntity.owners.length > 0)) && {
				owners: currentEntity.owners,
			}),
		...(currentEntity &&
			((relevantChanges?.includes('owner_group_ids') ?? false) ||
				(currentEntity.owners_groups &&
					currentEntity.owners_groups.length > 0)) && {
				owners_groups: currentEntity.owners_groups,
			}),
		...(currentEntity &&
			(relevantChanges?.includes('collection_ids') ||
				(currentEntity.collections &&
					currentEntity.collections.length > 0)) && {
				collections: currentEntity.collections,
			}),
		...(currentEntity &&
			currentEntity.properties2 &&
			Object.keys(currentEntity.properties2).length > 0 && {
				properties2: currentEntity.properties2,
			}),
	} as ISecodaEntity;

	const { data: customProperties } = useListCustomProperties({});
	const filteredCustomProperties = useMemo(
		() =>
			customProperties
				?.filter((cp) =>
					cp.entity_types.includes(versionHistory.secoda_entity_type ?? '')
				)
				.filter(
					(cp) =>
						currentValues.custom_properties &&
						!!currentValues.custom_properties[cp.id]
				),
		[
			customProperties,
			versionHistory.secoda_entity_type,
			currentValues.custom_properties,
		]
	);

	const properties = (
		Object.keys(consolidatedEntity) as (keyof SecodaEntityPropertySnapshot)[]
	)
		// this check is to ensure that we don't show properties when we don't have a snapshot
		// a snapshot allows us to show a consolidated view of the entity at that point in time
		// without a snapshot, we only have the current values, which may not be complete
		.filter(
			(property) =>
				VALID_PROPERTIES.includes(property) &&
				(snapshot || !isNil(consolidatedEntity[property]))
		);

	if (properties.length === 0) {
		return null;
	}

	return (
		<Stack spacing="sm">
			<Text size="sm" color="text/secondary/default">
				Properties
			</Text>
			<Group spacing="3xs">
				{properties.includes('published') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'published',
							!equal(previousValues.published, currentValues.published)
						)}
					>
						<StatusPropertySelector
							entity={consolidatedEntity}
							viewerOfEntity={true}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('teams') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'team_ids',
							!equal(previousValues.team_ids, currentValues.team_ids)
						)}
					>
						<TeamsPropertySelector
							entity={{
								...consolidatedEntity,
								teams: consolidatedEntity.teams ?? [],
							}}
							viewerOfEntity={true}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('tags') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'tag_ids',
							!equal(previousValues.tag_ids, currentValues.tag_ids)
						)}
					>
						<TagPropertySelector
							key={consolidatedEntity.tags?.join(',') ?? 'tags'}
							initialValue={consolidatedEntity.tags ?? []}
							readOnly
							placeholder="No tags"
							placeholderIconName="tag"
							forceVariant="tertiary"
							forceSourceTags={!!integration?.force_source_tags}
							entityIntegrationId={integration?.id}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('collections') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'collection_ids',
							!equal(
								previousValues.collection_ids,
								currentValues.collection_ids
							)
						)}
					>
						<CollectionsPropertySelector
							key={consolidatedEntity.collections?.join(',') ?? 'collections'}
							entity={{
								...consolidatedEntity,
								collections: consolidatedEntity.collections ?? [],
							}}
							preventNavigate={true}
							viewerOfEntity={true}
						/>
					</PropertyWrapper>
				)}
				{(properties.includes('owners') ||
					properties.includes('owners_groups')) && (
					<PropertyWrapper
						hasChanged={
							shouldShowAsChanged(
								'owner_ids',
								!equal(previousValues.owner_ids, currentValues.owner_ids)
							) ||
							shouldShowAsChanged(
								'owner_group_ids',
								!equal(
									previousValues.owner_group_ids,
									currentValues.owner_group_ids
								)
							)
						}
					>
						<OwnersPropertySelector
							key={`${consolidatedEntity.owners?.join(',') ?? 'owners'}${consolidatedEntity.owners_groups?.join(',') ?? 'owners_groups'}`}
							entity={{
								...consolidatedEntity,
								owners: consolidatedEntity.owners ?? [],
								owners_groups: consolidatedEntity.owners_groups ?? [],
							}}
							integration={integration}
							viewerOfEntity={true}
							handleEntityChange={() => () => {}}
							preventNavigate={true}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('pii') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'pii',
							!equal(previousValues.pii, currentValues.pii)
						)}
					>
						<GovernancePropertySelector
							entity={consolidatedEntity}
							viewerOfEntity={true}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('verified') && (
					<PropertyWrapper
						hasChanged={shouldShowAsChanged(
							'verified',
							!equal(previousValues.verified, currentValues.verified)
						)}
					>
						<VerificationPropertySelector
							entity={consolidatedEntity}
							viewerOfEntity={true}
						/>
					</PropertyWrapper>
				)}
				{properties.includes('properties2') &&
					filteredCustomProperties &&
					filteredCustomProperties.map((custom_property) => (
						<PropertyWrapper
							hasChanged={shouldShowAsChanged(
								`custom_properties.${custom_property.id}`,
								!equal(
									previousValues.custom_properties?.[custom_property.id],
									currentValues.custom_properties?.[custom_property.id]
								)
							)}
							key={custom_property.id}
						>
							<CustomPropertiesPropertySelector
								entity={consolidatedEntity}
								customProperty={custom_property}
								readOnly={true}
							/>
						</PropertyWrapper>
					))}
			</Group>
		</Stack>
	);
}
